import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Card, RadioButton, Section } from '@factorialco/deprecated-design-system'
import { SettingPageEmployeesSettingFragment } from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import ManageSettingVersionModal from 'modules/attendance/components/SettingItemPage/TimeTracking/ManageSettingVersionModal'
import useUpdateAttendanceSetting from 'modules/attendance/components/SettingItemPage/TimeTracking/hooks/useUpdateAttendanceSetting'
import AttendanceSettingForm from 'modules/attendance/lib/forms/AttendanceSettingForm'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'
import { ExtraHoursBaselineOptions } from 'modules/attendance/store/models/AttendanceSetting'

export interface Props {
  attendanceSetting: AttendanceSetting
}

const ExtraHoursBaseline = ({ attendanceSetting }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { PLANNED_HOURS, CONTRACT_HOURS } = ExtraHoursBaselineOptions

  const createSettingForm = (setting: AttendanceSetting) =>
    new AttendanceSettingForm({
      extra_hours_baseline: setting.attributes.extra_hours_baseline,
    })

  const [settingForm, setSettingForm] = React.useState(createSettingForm(attendanceSetting))
  const extraHoursBaselineField = settingForm.get('extra_hours_baseline')

  const { loading, updateSetting } = useUpdateAttendanceSetting(settingForm, attendanceSetting)
  const [selected, setSelected] = React.useState(attendanceSetting.extraHoursBaseline)
  const [openEffectiveAtFormModal, setOpenEffectiveAtFormModal] = React.useState(false)

  const onSubmit = () => {
    track(trackingEvents.timeTracking.changeReferencePoint, {
      option: selected,
    })
    updateSetting()
    onClose()
  }

  const onCreateSetting = async (newSetting: SettingPageEmployeesSettingFragment) => {
    const settingId = +newSetting.id

    onSubmit()

    const routeParams = {
      id: 'time_tracking',
      settingId: settingId.toString(),
      tabId: 'work_time_categories',
    }
    navigate(navigation.settings.time_tracking.settingId.tabId.fullPath(routeParams))
  }

  useEffect(() => {
    setSettingForm(createSettingForm(attendanceSetting))
    setSelected(attendanceSetting.extraHoursBaseline)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendanceSetting.attributes.id])

  const onChange = (option: string) => {
    extraHoursBaselineField.set(option)
    setSelected(option)
  }

  const onClose = () => {
    setOpenEffectiveAtFormModal(false)
  }

  return (
    <Section
      illustration="performance"
      title={i18n.t(
        'time_tracking.settings.work_time_categories.extra_hours_baseline.section.title'
      )}
      description={i18n.t(
        'time_tracking.settings.work_time_categories.extra_hours_baseline.section.description'
      )}
    >
      <Box gap="s8">
        <Card>
          <Card.Title
            size="small"
            action={
              <RadioButton
                size="medium"
                name="planned_hours"
                onChange={() => onChange(PLANNED_HOURS)}
                value={PLANNED_HOURS}
                checked={selected === PLANNED_HOURS}
              />
            }
          >
            {i18n.t(
              'time_tracking.settings.work_time_categories.extra_hours_baseline.section.planned_hours'
            )}
          </Card.Title>
          <Card.Description>
            {i18n.t(
              'time_tracking.settings.work_time_categories.extra_hours_baseline.section.planned_hours_description'
            )}
          </Card.Description>
        </Card>
        <Card>
          <Card.Title
            size="small"
            action={
              <RadioButton
                size="medium"
                name={CONTRACT_HOURS}
                onChange={() => onChange(CONTRACT_HOURS)}
                value={CONTRACT_HOURS}
                checked={selected === CONTRACT_HOURS}
              />
            }
          >
            {i18n.t(
              'time_tracking.settings.work_time_categories.extra_hours_baseline.section.agreement_hours'
            )}
          </Card.Title>
          <Card.Description>
            {i18n.t(
              'time_tracking.settings.work_time_categories.extra_hours_baseline.section.agreement_hours_description'
            )}
          </Card.Description>
        </Card>
        {settingForm.isDirty && (
          <Box alignItems="flexEnd">
            <Button type="primary" onClick={() => setOpenEffectiveAtFormModal(true)}>
              {i18n.t('common.submit')}
            </Button>
          </Box>
        )}
        {openEffectiveAtFormModal && (
          <ManageSettingVersionModal
            onCreate={onCreateSetting}
            onUpdate={onSubmit}
            onClose={onClose}
            running={loading}
            attendanceSettingId={attendanceSetting.attributes.id}
            attendanceSettingEffectiveAt={attendanceSetting.attributes.effective_at}
          />
        )}
      </Box>
    </Section>
  )
}

export default ExtraHoursBaseline
